import Button from "components/Button/Button";
import Card from "components/Card/Card";
import type { FileType } from "types/DataTypes";

import KaboodleModal from "./KaboodleModal";

interface ExistingClaimFileModalProps {
  isOpen: boolean;
  claimNumber: string;
  // The parent only renders this modal once a match exists, so this is
  // always the matched file (never null).
  matchedFile: FileType;
  matchCount: number;
  onUseExisting: () => void;
  onImport: () => void;
  onDismiss: () => void;
}

const claimantName = (file: FileType): string | null => {
  const claimant = file.claimants?.[0];
  if (!claimant) return null;
  return [claimant.first_name, claimant.last_name].filter(Boolean).join(" ");
};

/**
 * Shown while creating a new file when the entered claim number already
 * exists on another file. Lets the user jump to the existing file, pre-fill
 * the new-file form from it, or continue creating a brand-new file.
 */
const ExistingClaimFileModal = ({
  isOpen,
  claimNumber,
  matchedFile,
  matchCount,
  onUseExisting,
  onImport,
  onDismiss,
}: ExistingClaimFileModalProps) => {
  const client = matchedFile.client?.name;
  const claimant = claimantName(matchedFile);

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onDismiss}
      footer={
        <div className="flex jc-c">
          <Button onClick={onUseExisting}>Use Existing File</Button>
          <Button onClick={onImport}>Import</Button>
          <Button onClick={onDismiss}>New File</Button>
        </div>
      }
    >
      <Card heading="Claim Number Already Exists">
        <p>
          Claim number <strong>{claimNumber}</strong> already exists on{" "}
          {matchCount > 1 ? (
            <>
              <strong>{matchCount}</strong> existing files
            </>
          ) : (
            "an existing file"
          )}
          .
        </p>
        <ul className="mrg-top">
          <li>File #{matchedFile.id}</li>
          {client && <li>Client: {client}</li>}
          {claimant && <li>Claimant: {claimant}</li>}
          {matchedFile.status_verbose && (
            <li>Status: {matchedFile.status_verbose}</li>
          )}
        </ul>
        <p className="mrg-top">
          Open the existing file, import its details into this new file, or
          continue creating a new file.
        </p>
      </Card>
    </KaboodleModal>
  );
};

export default ExistingClaimFileModal;
